import { memo, useMemo } from 'react'

import { MobileBottomNavigation } from '@hhgtech/hhg-components'
import { LOCALE_HAS_COMMUNITY_SITE } from '@hhgtech/hhg-components/constants'
import { Stethoscope, Heart } from '@hhgtech/icons/other'
import { ChatAi } from '@hhgtech/icons/other'
import { ArticleSvg, CommunitySvg, ScaleSvg } from '@hhgtech/icons/together'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { useSpacingSubotStickyBar } from 'hooks/useSpacingSubotStickyBar'
import {
  CHAT_AI_PATH,
  CARE_LOCALES_BOTTOM_NAV,
  HAS_CHAT_AI_SITE,
  LOCALE_SPECS,
  MY_HEALTH_ROUTES,
  SSO_URL,
} from 'interfaces/constants'
import { LOCALE } from 'interfaces/types'

import MobileBottomArticleNavigation from './article'
import MobileBottomFitnessHealthToolNavigation from './fitnessTool'
import MobileBottomHealthToolNavigation from './HealthTool'
import { StyledMobileBottomNavigation } from './index.styled'

type MobileBottomNavigationGlobalWrapperProps = Record<string, never>
const MobileBottomNavigationGlobalWrapper = memo(() => {
  const { locale, pathname } = useRouterExtend()
  const { formatMessage: f } = useIntl()

  useSpacingSubotStickyBar()

  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])

  const haveCommunity = useMemo(() => {
    return LOCALE_HAS_COMMUNITY_SITE.includes(locale as LOCALE)
  }, [locale])

  const haveCare = useMemo(() => {
    return CARE_LOCALES_BOTTOM_NAV.includes(locale as LOCALE)
  }, [locale])

  const haveChatAi = useMemo(() => {
    return HAS_CHAT_AI_SITE.includes(locale as LOCALE)
  }, [locale])

  const isHealthToolActive = useMemo(() => {
    return pathname.split('/').includes('health-tools')
  }, [pathname])

  // if (locale === LOCALE.Vietnam) {
  //   return <HomeMobileNavigationBottom />
  // }

  return (
    <>
      <StyledMobileBottomNavigation isCollapsed={false}>
        <a
          href={currentDomain + '/categories'}
          className="pointer-event-child-none"
          data-event-category="Sticky Bottom"
          data-event-action="Home Click"
          data-event-label={currentDomain + '/categories'}
        >
          <MobileBottomNavigation.Icon
            icon={<ArticleSvg isActive={pathname === '/categories'} />}
            label={f({
              id: 'mobileBar.home',
              defaultMessage: 'Home',
            })}
            isActive={pathname === '/categories'}
          />
        </a>
        <a
          href={currentDomain + '/health-tools'}
          className="pointer-event-child-none"
          data-event-category="Sticky Bottom"
          data-event-action="Engage Click"
          data-event-label={currentDomain + '/health-tools'}
        >
          <MobileBottomNavigation.Icon
            icon={<ScaleSvg isActive={isHealthToolActive} />}
            label={f({
              id: 'mobileBar.healthTools',
              defaultMessage: 'Health Screening',
            })}
            isActive={isHealthToolActive}
          />
        </a>
        {haveCare && (
          <a
            href={currentDomain + '/care'}
            className="pointer-event-child-none"
            data-event-category="Sticky Bottom"
            data-event-action="Care Click"
            data-event-label={currentDomain + '/care'}
          >
            <MobileBottomNavigation.Icon
              icon={<Stethoscope />}
              label={f({
                id: 'mobileBar.care',
                defaultMessage: 'Care',
              })}
            />
          </a>
        )}
        {haveChatAi && (
          <a
            href={currentDomain + CHAT_AI_PATH}
            className="pointer-event-child-none"
            data-event-category="Chatbot AI"
            data-event-action="Open"
            data-event-label="Button Menu"
          >
            <MobileBottomNavigation.Icon
              icon={<ChatAi size={24} color="#2F8AF2" />}
              label={`${LOCALE_SPECS[locale].SITE_NAME} AI`}
            />
          </a>
        )}
        {haveCommunity && (
          <a
            href={currentDomain + '/community'}
            className="pointer-event-child-none"
            data-event-category="Sticky Bottom"
            data-event-action="Together Click"
            data-event-label={currentDomain + '/community'}
          >
            <MobileBottomNavigation.Icon
              icon={<CommunitySvg size={24} />}
              label={f({
                id: 'mobileBar.community',
                defaultMessage: 'Community',
              })}
            />
          </a>
        )}
        {!haveCare && !haveCommunity && (
          <a
            href={`${SSO_URL}${MY_HEALTH_ROUTES.MY_HEALTH_TRACKER}`}
            className="pointer-event-child-none"
            data-event-category="Sticky Bottom"
            data-event-action="User Profile Click"
            data-event-label={`${SSO_URL}${MY_HEALTH_ROUTES.MY_HEALTH_TRACKER}`}
          >
            <MobileBottomNavigation.Icon
              icon={<Heart />}
              label={f({
                id: 'mobileBar.myHealth',
                defaultMessage: 'My Health',
              })}
            />
          </a>
        )}
      </StyledMobileBottomNavigation>
    </>
  )
})

interface CompoundedComponent
  extends React.ForwardRefExoticComponent<MobileBottomNavigationGlobalWrapperProps> {
  Article: typeof MobileBottomArticleNavigation
  HealthTool: typeof MobileBottomHealthToolNavigation
  FitnessHealthTool: typeof MobileBottomFitnessHealthToolNavigation
}

const MobileBottomNavigationWrapper = (MobileBottomNavigationGlobalWrapper as unknown) as CompoundedComponent

MobileBottomNavigationWrapper.Article = MobileBottomArticleNavigation
MobileBottomNavigationWrapper.HealthTool = MobileBottomHealthToolNavigation
MobileBottomNavigationWrapper.FitnessHealthTool = MobileBottomFitnessHealthToolNavigation

export default MobileBottomNavigationWrapper
